import apiClient from "./apiClient";
const API_URL = `/ipd-admissions`; // Use relative path since apiClient has baseURL

const toPositiveId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    if (Array.isArray(value)) return null;
    return toPositiveId(
      value.id ??
        value.admissionId ??
        value.admission_id ??
        value.prescriptionId ??
        value.prescription_id
    );
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

const copyIfPresent = (target, source, camel, snake) => {
  const val = source?.[camel] ?? (snake ? source?.[snake] : undefined);
  if (val == null || val === "") return;
  target[camel] = val;
  if (snake) target[snake] = val;
};

/** Keep existing admission fields so PUT does not wipe ward/bed/status. */
const buildAdmissionPreserveFields = (admission = {}) => {
  const fields = {};
  copyIfPresent(fields, admission, "patientId", "patient_id");
  copyIfPresent(fields, admission, "doctorId", "doctor_id");
  copyIfPresent(fields, admission, "wardId", "ward_id");
  copyIfPresent(fields, admission, "bedId", "bed_id");
  copyIfPresent(fields, admission, "admissionDate", "admission_date");
  copyIfPresent(fields, admission, "admissionType", "admission_type");
  copyIfPresent(fields, admission, "departmentId", "department_id");
  copyIfPresent(fields, admission, "status");
  if (admission.isDischarge != null) fields.isDischarge = admission.isDischarge;
  if (admission.is_discharge != null) fields.isDischarge = admission.is_discharge;
  return fields;
};

export const create = async (admissionData) => {
  // Remove organizationId from data if it exists (apiClient will add it as query param for POST requests)
  // This ensures the backend routes to the correct organization-specific table
  const { organizationId, ...admissionPayload } = admissionData;
  
  // apiClient will automatically add organizationId as query parameter via interceptor
  // This ensures records are created in organization-specific tables (e.g., 23_ipd_admissions)
  // instead of the base ipd_admissions table
  const response = await apiClient.post(`${API_URL}/create`, admissionPayload);
  return response.data;
};

export const getAdmissionByPatientId = async (patientId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/patient/${patientId}`);
    return response.data;
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet
    if (
      error.response?.status === 500 &&
      error.response?.data?.message?.includes("Failed to retrieve") &&
      error.response?.data?.message?.includes("from table")
    ) {
      console.warn(
        "Organization-specific IPD admission table not found for patient, returning empty array:",
        error.response.data.message
      );
      // Return empty array if table doesn't exist so UI can handle 'no admissions' gracefully
      return [];
    }
    console.error("Error fetching IPD admissions by patientId:", error);
    throw error;
  }
};

export const getAdmissionsByDoctorId = async (doctorId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/doctor/${doctorId}`);
    return response.data;
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet
    if (
      error.response?.status === 500 &&
      error.response?.data?.message?.includes("Failed to retrieve") &&
      error.response?.data?.message?.includes("from table")
    ) {
      console.warn(
        "Organization-specific IPD admission table not found for doctor, returning empty array:",
        error.response.data.message
      );
      return [];
    }
    console.error("Error fetching IPD admissions by doctorId:", error);
    throw error;
  }
};

export const getAll = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(API_URL);
    return response.data;
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet
    if (
      error.response?.status === 500 &&
      error.response?.data?.message?.includes("Failed to retrieve") &&
      error.response?.data?.message?.includes("from table")
    ) {
      console.warn(
        "Organization-specific IPD admission table not found, returning empty array:",
        error.response.data.message
      );
      return []; // Return empty array if table doesn't exist
    }
    console.error("Error fetching IPD admissions:", error);
    throw error;
  }
};

export const getById = async (id) => {
  // apiClient will automatically add organizationId as query param via interceptor
  const response = await apiClient.get(`${API_URL}/${id}`);
  return response.data;
};

export const getAllWards = async () => {
  // apiClient will automatically add organizationId as query param via interceptor
  const response = await apiClient.get(API_URL);
  return response.data;
};

export const updateStatusById = async (id, admission) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(`${API_URL}/${id}/status`, admission);
    return response.data;
  } catch (error) {
    console.error("Error updating appointment:", error);
    if (error.response) {
      console.error("Server response:", error.response.data);
    }
    throw error;
  }
};

export const updateAdmission = async (id, admission) => {
  if (!id) {
    throw new Error("Invalid admission ID");
  }
  const { organizationId, ...admissionData } = admission || {};
  if (admissionData.prescriptionId != null && admissionData.prescriptionId !== "") {
    const prescriptionId = Number(admissionData.prescriptionId);
    if (Number.isFinite(prescriptionId) && prescriptionId > 0) {
      admissionData.prescriptionId = prescriptionId;
      admissionData.prescription_id = prescriptionId;
    }
  } else if (
    admissionData.prescription_id != null &&
    admissionData.prescription_id !== ""
  ) {
    const prescriptionId = Number(admissionData.prescription_id);
    if (Number.isFinite(prescriptionId) && prescriptionId > 0) {
      admissionData.prescriptionId = prescriptionId;
      admissionData.prescription_id = prescriptionId;
    }
  }
  const response = await apiClient.put(`${API_URL}/${id}`, admissionData);
  return response.data;
};

/**
 * After IPD prescription create, store prescription_id on the admission.
 * Preserves existing admission fields. Caller should treat failures as non-fatal.
 */
export const linkPrescriptionToAdmission = async (
  admissionId,
  prescriptionId,
  admissionSnapshot = null
) => {
  const admId = toPositiveId(admissionId);
  const rxId = toPositiveId(prescriptionId);
  if (!admId || !rxId) return null;

  let snapshot =
    admissionSnapshot && typeof admissionSnapshot === "object"
      ? admissionSnapshot
      : null;

  if (!snapshot) {
    try {
      snapshot = await getById(admId);
    } catch (lookupError) {
      console.warn(
        "[PatientAdmissionService] Could not load admission before linking prescription:",
        lookupError
      );
      snapshot = null;
    }
  }

  const payload = {
    ...buildAdmissionPreserveFields(snapshot || {}),
    prescriptionId: rxId,
    prescription_id: rxId,
  };

  console.log(
    `[PatientAdmissionService] linkPrescriptionToAdmission: admissionId=${admId}, prescriptionId=${rxId}`
  );
  return updateAdmission(admId, payload);
};

export default {
  create,
  getAllWards,
  getAdmissionByPatientId,
  updateStatusById,
  updateAdmission,
  linkPrescriptionToAdmission,
  getById,
};